import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";

interface BubbleProps {
  label: string;
  size: number;
  x: number;
  y: number;
  delay: number;
  scale: number;
}

function Bubble({ label, size, x, y, delay, scale }: BubbleProps) {
  const s = size * scale;
  const sx = x * scale;
  const sy = y * scale;
  return (
    <div
      className="absolute flex items-center justify-center rounded-full border border-accent/40 bg-background text-center transition-transform duration-700 ease-out cursor-default select-none"
      style={{
        width: s,
        height: s,
        left: `calc(50% + ${sx}px - ${s / 2}px)`,
        top: `calc(50% + ${sy}px - ${s / 2}px)`,
        animationDelay: `${delay}s`,
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        const angle = Math.random() * 360;
        const dist = 3 + Math.random() * 3;
        const dx = Math.cos(angle * Math.PI / 180) * dist;
        const dy = Math.sin(angle * Math.PI / 180) * dist;
        el.style.transform = `translate(${dx}px, ${dy}px)`;
        setTimeout(() => {
          el.style.transform = `translate(${-dx * 0.5}px, ${-dy * 0.5}px)`;
          setTimeout(() => {
            el.style.transform = 'translate(0, 0)';
          }, 350);
        }, 350);
      }}
    >
      <span
        className="font-medium text-foreground leading-tight px-1"
        style={{ fontSize: Math.max(9, 13 * scale) }}
      >
        {label}
      </span>
    </div>
  );
}

export function EcosystemGraphic() {
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  const scale = isMobile ? 0.55 : 1;

  const bubbles: Omit<BubbleProps, 'delay' | 'scale'>[] = language === 'de' ? [
    { label: "Meet &\nGreet", size: 90, x: -80, y: -140 },
    { label: "BTS", size: 80, x: 110, y: -100 },
    { label: "Real-Life\nEvents", size: 105, x: -140, y: -10 },
    { label: "Premieren", size: 100, x: -90, y: 130 },
    { label: "Close\nCircles", size: 100, x: 90, y: 100 },
    { label: "PR-Stories", size: 110, x: -60, y: -260 },
    { label: "Paid-\nKampagnen", size: 115, x: 160, y: -230 },
    { label: "Pop-Ups", size: 95, x: 260, y: -60 },
    { label: "Charakter-\nwelten", size: 120, x: 250, y: 110 },
    { label: "Soundtracks", size: 115, x: 50, y: 270 },
    { label: "Product-\nDrops", size: 110, x: -230, y: 190 },
    { label: "WhatsApp-\nGruppen", size: 110, x: -280, y: -80 },
  ] : [
    { label: "Meet &\nGreet", size: 90, x: -80, y: -140 },
    { label: "BTS", size: 80, x: 110, y: -100 },
    { label: "Real-Life\nEvents", size: 105, x: -140, y: -10 },
    { label: "Premieres", size: 100, x: -90, y: 130 },
    { label: "Close\nCircles", size: 100, x: 90, y: 100 },
    { label: "PR Stories", size: 110, x: -60, y: -260 },
    { label: "Paid\nCampaigns", size: 115, x: 160, y: -230 },
    { label: "Pop-Ups", size: 95, x: 260, y: -60 },
    { label: "Character\nWorlds", size: 120, x: 250, y: 110 },
    { label: "Soundtracks", size: 115, x: 50, y: 270 },
    { label: "Product\nDrops", size: 110, x: -230, y: 190 },
    { label: "WhatsApp\nGroups", size: 110, x: -280, y: -80 },
  ];

  const centerSize = 130 * scale;

  return (
    <div className="relative w-full max-w-[700px] mx-auto" style={{ aspectRatio: '1 / 1' }}>
      {/* Outer glow ring */}
      <div
        className="absolute rounded-full"
        style={{
          width: '85%',
          height: '85%',
          left: '7.5%',
          top: '7.5%',
          background: 'radial-gradient(circle, transparent 45%, hsl(var(--accent) / 0.15) 55%, hsl(var(--accent) / 0.08) 70%, transparent 85%)',
        }}
      />
      {/* Inner glow ring */}
      <div
        className="absolute rounded-full"
        style={{
          width: '45%',
          height: '45%',
          left: '27.5%',
          top: '27.5%',
          background: 'radial-gradient(circle, transparent 40%, hsl(var(--accent) / 0.25) 60%, hsl(var(--accent) / 0.1) 80%, transparent 95%)',
        }}
      />
      {/* Center circle */}
      <div
        className="absolute rounded-full border-2 border-accent bg-background flex items-center justify-center"
        style={{
          width: centerSize,
          height: centerSize,
          left: `calc(50% - ${centerSize / 2}px)`,
          top: `calc(50% - ${centerSize / 2}px)`,
        }}
      >
        <span
          className="font-display font-bold text-foreground text-center leading-tight"
          style={{ fontSize: Math.max(12, 20 * scale) }}
        >
          Vertical<br />Drama
        </span>
      </div>
      {/* Bubbles */}
      {bubbles.map((bubble, i) => (
        <Bubble
          key={bubble.label}
          {...bubble}
          scale={scale}
          delay={i * 0.1}
        />
      ))}
    </div>
  );
}
